/**
 * Sprint V101.d · Admin-Cockpit (Sa 20.09.2026, Andreas).
 *
 * Ein Ort für den Admin-Token, den alle Admin-Panels teilen. Vorher hat
 * jede Panel-Komponente ihren eigenen localStorage-Key gepflegt — was zu
 * inkonsistentem Verhalten führte (Admin-Token in Bootstrap-Tool ≠ Token
 * im Webinar-Panel). Jetzt: Ein Key, eine Quelle, ein Formular-Feld im
 * `<AdminLayout>` Sidebar-Footer.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "f4s_admin_bootstrap_token"; // legacy-kompatibel

const AdminTokenContext = createContext({
  token: "",
  setToken: () => {},
  clear: () => {},
});

export function AdminTokenProvider({ children }) {
  const [token, setTokenState] = useState("");

  useEffect(() => {
    try {
      const t = localStorage.getItem(STORAGE_KEY) || "";
      if (t) setTokenState(t);
    } catch {}
  }, []);

  const setToken = useCallback((newToken) => {
    const cleaned = String(newToken || "").trim();
    setTokenState(cleaned);
    try {
      if (cleaned) localStorage.setItem(STORAGE_KEY, cleaned);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const clear = useCallback(() => setToken(""), [setToken]);

  const value = useMemo(
    () => ({ token, setToken, clear }),
    [token, setToken, clear],
  );

  return (
    <AdminTokenContext.Provider value={value}>
      {children}
    </AdminTokenContext.Provider>
  );
}

export function useAdminToken() {
  return useContext(AdminTokenContext);
}
